import React, { useState, useEffect } from "react";
import { Search, RotateCcw, FileSpreadsheet, Edit3 } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import { useLanguage } from "../../context/LanguageContext";
import { API } from "../../utils";

const Reports = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });
  const [shiftFilter, setShiftFilter] = useState("All"); // All | Day Shift | Night Shift

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchReports = async (isPagination = false) => {
    if (!isPagination) setLoading(true);
    try {
      const query = new URLSearchParams();
      if (filters.fromDate) query.append("startDate", filters.fromDate);
      if (filters.toDate) query.append("endDate", filters.toDate);
      if (shiftFilter && shiftFilter !== "All" && shiftFilter !== t("common.all")) query.append("shift", shiftFilter);
      query.append("page", page);
      query.append("limit", limit);
      
      const response = await API.get(`/api/reports?${query.toString()}`);
      const result = response.data;
      if (result.success && result.data) {
        setEntries(result.data);
        setTotalPages(result.totalPages || 1);
        setTotalCount(result.totalCount || result.data.length);
      }
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.error || t("reports.fetchError");
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(true);
    // eslint-disable-next-line
  }, [page, limit, shiftFilter]);

  const handleSearch = () => {
    fetchReports(false);
  };

  const handleReset = () => {
    setFilters({ fromDate: "", toDate: "" });
    setShiftFilter("All");
    setPage(1);
    setTimeout(() => {
      fetchReports(false);
    }, 0);
  };

  const handleExport = () => {
    const csvRows = [];
    
    // 1. Company Header
    csvRows.push(t("reports.reportHeader"));
    csvRows.push(`${t("reports.reportPeriod")}: ${filters.fromDate || t("common.all")} to ${filters.toDate || t("common.all")}`);
    csvRows.push(`${t("reports.shiftFilter")}: ${shiftFilter}`);
    csvRows.push(""); // Empty line

    // 2. Table Headers
    const headers = [
      t("common.date"), t("common.shift"), t("dailyEntry.savedBy"), 
      `${t("common.petrol")} ${t("common.liters")}`, `${t("common.petrol")} ${t("common.amount")} (INR)`, 
      `${t("common.diesel")} ${t("common.liters")}`, `${t("common.diesel")} ${t("common.amount")} (INR)`, 
      `${t("common.oil")} 20ml (${t("common.pcs")})`, `${t("common.oil")} 40ml (${t("common.pcs")})`, `${t("common.oil")} 60ml (${t("common.pcs")})`, `${t("common.oil")} ${t("common.amount")} (INR)`, 
      `${t("common.cash")} (INR)`, `${t("common.online")} (INR)`, `${t("common.credit")} (INR)`,
      `Sales Total (INR)`, `Receipt Amount (INR)`, `Collection (INR)`
    ];
    csvRows.push(headers.join(","));

    // 3. Data Rows — export only what is currently visible (respects shift filter)
    entries.forEach(row => {
      const oAmt = (row.oil?.["20ml"]?.amount || 0) + (row.oil?.["40ml"]?.amount || 0) + (row.oil?.["60ml"]?.amount || 0);
      const sTotal = row.totals?.salesTotal || row.totals?.grandTotal || 0;
      const fColl = row.totals?.finalCollectionAmount || sTotal + (row.payment?.receipt || 0);
      
      const data = [
        new Date(row.date).toLocaleDateString('en-GB'),
        row.shift,
        row.updatedBy || row.createdBy || 'System',
        row.petrol?.sold || 0,
        row.petrol?.amount || 0,
        row.diesel?.sold || 0,
        row.diesel?.amount || 0,
        row.oil?.["20ml"]?.sold || 0,
        row.oil?.["40ml"]?.sold || 0,
        row.oil?.["60ml"]?.sold || 0,
        oAmt,
        row.payment?.cash || 0,
        row.payment?.online || 0,
        row.payment?.credit || 0,
        sTotal,
        row.payment?.receipt || 0,
        fColl
      ];
      csvRows.push(data.join(","));
    });

    // 4. Totals Row
    csvRows.push(""); // Empty line before total
    const totalData = [
      t("common.total"), "", "", 
      totals.pSold, totals.pAmt, 
      totals.dSold, totals.dAmt, 
      totals.o20, totals.o40, totals.o60, totals.oAmt, 
      totals.cash, totals.online, totals.credit, 
      totals.salesTotal, totals.receipt, totals.finalCollection
    ];
    csvRows.push(totalData.join(","));

    // Create download link
    const blob = new Blob([csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kaviraj_Agencies_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate totals dynamically using entries
  const totals = entries.reduce(
    (acc, row) => {
      const sTotal = row.totals?.salesTotal || row.totals?.grandTotal || 0;
      const rAmt = row.payment?.receipt || 0;
      const fColl = row.totals?.finalCollectionAmount || (sTotal + rAmt);
      
      acc.pSold += row.petrol?.sold || 0;
      acc.pAmt += row.petrol?.amount || 0;
      acc.dSold += row.diesel?.sold || 0;
      acc.dAmt += row.diesel?.amount || 0;
      acc.o20 += row.oil?.["20ml"]?.sold || 0;
      acc.o40 += row.oil?.["40ml"]?.sold || 0;
      acc.o60 += row.oil?.["60ml"]?.sold || 0;
      acc.oAmt += (row.oil?.["20ml"]?.amount || 0) + (row.oil?.["40ml"]?.amount || 0) + (row.oil?.["60ml"]?.amount || 0);
      acc.cash += row.payment?.cash || 0;
      acc.online += row.payment?.online || 0;
      acc.credit += row.payment?.credit || 0;
      acc.receipt += rAmt;
      acc.salesTotal += sTotal;
      acc.finalCollection += fColl;
      return acc;
    },
    { pSold: 0, pAmt: 0, dSold: 0, dAmt: 0, o20: 0, o40: 0, o60: 0, oAmt: 0, cash: 0, online: 0, credit: 0, receipt: 0, salesTotal: 0, finalCollection: 0 }
  );

  const SHIFT_OPTIONS = ["All", "Day Shift", "Intermediate Shift"];

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-10">
      <h2 className="text-xl font-bold text-gray-800 mb-2">{t("reports.title")}</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Top Filters */}
        <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Left Side: Date Filters and Action Buttons */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t("reports.fromDate")}</label>
              <input 
                type="date" 
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full sm:w-40 focus:ring-2 focus:ring-blue-100 outline-none transition-all" 
                value={filters.fromDate}
                onChange={(e) => setFilters({...filters, fromDate: e.target.value})}
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t("reports.toDate")}</label>
              <input 
                type="date" 
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full sm:w-40 focus:ring-2 focus:ring-blue-100 outline-none transition-all" 
                value={filters.toDate}
                onChange={(e) => setFilters({...filters, toDate: e.target.value})}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleSearch} 
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center space-x-2 shadow-sm transition-all active:scale-95"
              >
                <Search className="w-4 h-4" />
                <span>{t("common.search")}</span>
              </button>
              <button 
                onClick={handleReset} 
                className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-4 py-2 rounded-lg text-sm font-bold flex items-center space-x-2 shadow-sm transition-all active:scale-95"
              >
                <RotateCcw className="w-4 h-4" />
                <span>{t("common.reset")}</span>
              </button>
            </div>
          </div>

          {/* Right Side: Shift Filter and Export */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Shift Filter */}
            <div className="flex items-center bg-gray-100 p-1 rounded-xl w-full sm:w-auto">
              {SHIFT_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setShiftFilter(s)}
                  className={`flex-1 sm:flex-none px-4 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                    shiftFilter === s
                      ? s === "Day Shift"
                        ? "bg-yellow-400 text-yellow-900 shadow-sm"
                        : s === "Intermediate Shift"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-gray-800 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {s === "Day Shift" ? "☀️ Day Shift" : s === "Intermediate Shift" ? "🌙 Intermediate Shift" : t("common.all")}
                </button>
              ))}
            </div>

            <button 
              onClick={handleExport}
              disabled={entries.length === 0}
              className={`flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-bold flex items-center justify-center space-x-2 shadow-sm transition-all active:scale-95 ${entries.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>{t("reports.exportExcel")}</span>
            </button>
          </div>
        </div>

        {/* Results Section */}
        {loading ? (
          <Loader message={t("reports.generatingReport")} />
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t("reports.noRecordsFound")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-center border-collapse whitespace-nowrap">
              <thead>
                {/* Main Headers */}
                <tr>
                  <th rowSpan="2" className="border-b border-r border-gray-100 p-3 font-semibold text-gray-600 align-middle">{t("common.date")}</th>
                  <th rowSpan="2" className="border-b border-r border-gray-100 p-3 font-semibold text-gray-600 align-middle">{t("common.shift")}</th>
                  <th rowSpan="2" className="border-b border-r border-gray-100 p-3 font-semibold text-gray-600 align-middle">{t("dailyEntry.savedBy")}</th>
                  <th colSpan="2" className="border-b border-r border-gray-100 p-2 font-bold text-green-600">{t("common.petrol")}</th>
                  <th colSpan="2" className="border-b border-r border-gray-100 p-2 font-bold text-blue-600">{t("common.diesel")}</th>
                  <th colSpan="4" className="border-b border-r border-gray-100 p-2 font-bold text-orange-500">{t("common.oil")} ({t("common.pcs")})</th>
                  <th colSpan="3" className="border-b border-r border-gray-100 p-2 font-bold text-gray-700">{t("dailyEntry.payment")}</th>
                  <th rowSpan="2" className="border-b border-r border-gray-100 p-3 font-bold text-red-600 align-middle">Sales Total (₹)</th>
                  <th rowSpan="2" className="border-b border-r border-gray-100 p-3 font-bold text-gray-700 align-middle">Receipt (₹)</th>
                  <th rowSpan="2" className="border-b border-r border-gray-100 p-3 font-bold text-purple-700 align-middle">Collection (₹)</th>
                  <th rowSpan="2" className="border-b border-gray-100 p-3 font-semibold text-gray-600 align-middle">{t("common.actions")}</th>
                </tr>
                {/* Sub Headers */}
                <tr className="bg-gray-50">
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("dailyEntry.soldPcs")} (Ltr)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("common.amount")} (₹)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("dailyEntry.soldPcs")} (Ltr)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("common.amount")} (₹)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">20 ml</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">40 ml</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">60 ml</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("common.amount")} (₹)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("common.cash")} (₹)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("common.online")} (₹)</th>
                  <th className="border-b border-r border-gray-100 p-2 font-medium text-gray-500">{t("common.credit")} (₹)</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => {
                  const oAmt = (row.oil?.["20ml"]?.amount || 0) + (row.oil?.["40ml"]?.amount || 0) + (row.oil?.["60ml"]?.amount || 0);
                  const entryDate = new Date(row.date).toLocaleDateString('en-GB'); // format dd/mm/yyyy
                  const sTotal = row.totals?.salesTotal || row.totals?.grandTotal || 0;
                  const fColl = row.totals?.finalCollectionAmount || sTotal + (row.payment?.receipt || 0);

                  return (
                    <tr key={row._id} className="hover:bg-blue-50 border-b border-gray-50 transition-colors">
                      <td className="p-3 border-r border-gray-50 font-medium">{entryDate}</td>
                      <td className="p-3 border-r border-gray-50 font-medium text-gray-600">{row.shift}</td>
                      <td className="p-3 border-r border-gray-50 text-[10px] text-gray-500 font-semibold">{row.updatedBy || row.createdBy || 'System'}</td>
                      <td className="p-3 border-r border-gray-50">{row.petrol?.sold || 0}</td>
                      <td className="p-3 border-r border-gray-50">{(row.petrol?.amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50">{row.diesel?.sold || 0}</td>
                      <td className="p-3 border-r border-gray-50">{(row.diesel?.amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50">{row.oil?.["20ml"]?.sold || 0}</td>
                      <td className="p-3 border-r border-gray-50">{row.oil?.["40ml"]?.sold || 0}</td>
                      <td className="p-3 border-r border-gray-50">{row.oil?.["60ml"]?.sold || 0}</td>
                      <td className="p-3 border-r border-gray-50">{oAmt.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50">{(row.payment?.cash || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50">{(row.payment?.online || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50">{(row.payment?.credit || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50 font-bold text-red-600">{sTotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50 text-gray-600">{(row.payment?.receipt || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3 border-r border-gray-50 font-extrabold text-purple-700">{fColl.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      <td className="p-3">
                        <button 
                          onClick={() => {
                            const dateStr = row.date.split('T')[0];
                            const shiftStr = row.shift || "Day Shift";
                            navigate(`/daily-entry?date=${dateStr}&shift=${shiftStr}`);
                          }}
                          className="text-blue-500 hover:text-blue-700 bg-blue-50 p-1.5 rounded-full transition-colors"
                          title={t("common.edit")}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
                
                {/* Totals Row */}
                <tr className="bg-gray-50 font-bold">
                  <td className="p-3 border-r border-gray-100 text-gray-800">{t("common.total")}</td>
                  <td className="p-3 border-r border-gray-100"></td>
                  <td className="p-3 border-r border-gray-100"></td>
                  <td className="p-3 border-r border-gray-100 text-green-600">{totals.pSold.toLocaleString()}</td>
                  <td className="p-3 border-r border-gray-100 text-green-600">{totals.pAmt.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-blue-600">{totals.dSold.toLocaleString()}</td>
                  <td className="p-3 border-r border-gray-100 text-blue-600">{totals.dAmt.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-orange-500">{totals.o20.toLocaleString()}</td>
                  <td className="p-3 border-r border-gray-100 text-orange-500">{totals.o40.toLocaleString()}</td>
                  <td className="p-3 border-r border-gray-100 text-orange-500">{totals.o60.toLocaleString()}</td>
                  <td className="p-3 border-r border-gray-100 text-orange-500">{totals.oAmt.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-purple-600">{totals.cash.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-purple-600">{totals.online.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-purple-600">{totals.credit.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-red-600">{totals.salesTotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-gray-600">{totals.receipt.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 border-r border-gray-100 text-purple-700 font-extrabold">{totals.finalCollection.toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                  <td className="p-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        
        
        {/* Pagination Controls */}
        {totalCount > 0 && (
          <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between bg-white space-y-3 sm:space-y-0">
            <div className="flex items-center text-xs text-gray-500">
              <span>Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalCount)} of {totalCount} entries</span>
              <select 
                value={limit} 
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className="ml-3 border border-gray-200 rounded px-2 py-1 outline-none"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-center space-x-1">
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1 border border-gray-200 rounded text-xs font-semibold text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-colors"
              >
                Prev
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                    page === pageNum
                      ? "bg-blue-600 text-white shadow-sm"
                      : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1 border border-gray-200 rounded text-xs font-semibold text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
